import { AggregateByOption, BarChartScalesConfig, GroupByOption, MetricDatum, SortByOption } from '../../../types';
import {
  formatNumber,
  calculateYAxisWidth,
  calculateXAxisLabelSpacing,
  CHAR_WIDTH_PX,
  MINIMUM_TICK_WIDTH_STRAIGHT,
  MINIMUM_TICK_WIDTH_ROTATED,
  X_AXIS_LABEL_ANGLE_STRAIGHT,
  X_AXIS_LABEL_ANGLE_ROTATED,
} from '../../../utils/chartUtils';

const prepareGroupedChart = (
  data: MetricDatum[] | undefined,
  groupBy: GroupByOption[],
  stackGroups: string[][] | undefined,
  xAxisKeys: string[],
  yAxisKeys: string[],
  yAxisLabels: string[],
) => {
  const groupByKeys = groupBy.map((gb) => gb.key);

  // Create unique combinations of keys by their values
  const transformedData: Record<string, Record<string, number | string>> = {};
  const uniqueGroupByValues = new Set<string>();
  const newYAxisKeys: string[] = [];
  const newYAxisLabels: string[] = [];
  data?.forEach((datum) => {
    // Create a key from all x-axis values. This is used to track multi level groupings.
    const xAxisCombinedKey = xAxisKeys.map((key: string) => datum[key]).join(' - ');
    if (!transformedData[xAxisCombinedKey]) {
      // Initialize new group with x-axis values
      const newGroup: Record<string, any> = {};
      xAxisKeys.forEach((key: string) => {
        newGroup[key] = datum[key];
      });
      transformedData[xAxisCombinedKey] = newGroup;
    }

    const group = transformedData[xAxisCombinedKey];
    // Add y-axis values to the group
    yAxisKeys.forEach((yKey: string, index: number) => {
      const yValue = datum[yKey];
      if (yValue === undefined) {
        return;
      }

      // Get all group by values for this datum
      const groupByValues = groupByKeys
        .map((gbKey) => datum[gbKey])
        .filter((value) => value !== null && value !== undefined)
        .join(' - ');

      // Create the new key by combining y-axis name with group by values
      const newKey = groupByValues ? `${yKey} - ${groupByValues}` : yKey;
      const newLabel = groupByValues ? `${yAxisLabels[index]} - ${groupByValues}` : yAxisLabels[index];

      if (yValue !== null) {
        if (group && typeof group[newKey] === 'number') {
          group[newKey] = (group[newKey] as number) + (yValue as number);
        } else {
          group[newKey] = yValue;
        }
      }

      // Collect unique group by values for stack groups
      if (groupByValues) {
        uniqueGroupByValues.add(groupByValues);
      }

      // Add the new key to our list of y-axis keys if we haven't seen it before
      if (!newYAxisKeys.includes(newKey)) {
        newYAxisKeys.push(newKey);
        newYAxisLabels.push(newLabel);
      }
    });
  });

  return {
    data: Object.values(transformedData) as MetricDatum[],
    yAxisKeys: newYAxisKeys,
    yAxisLabels: newYAxisLabels,
    stackGroups: stackGroups?.length
      ? yAxisKeys.map((yKey: string) =>
          Array.from(uniqueGroupByValues).map((groupByValue) => `${yKey} - ${groupByValue}`),
        )
      : undefined,
  };
};

const sortData = (data: MetricDatum[], xAxisKeys: string[], sortBy?: SortByOption[]) => {
  if (!sortBy || sortBy.length === 0) {
    return data;
  }

  const preparedData = data.sort((a, b) => {
    // Sum all values from keys that start with the sortBy key
    const sortKey = sortBy[0].key;
    const aValue = Object.keys(a)
      .filter((key) => key.startsWith(sortKey))
      .reduce((sum, key) => sum + ((a[key] as number) || 0), 0);

    const bValue = Object.keys(b)
      .filter((key) => key.startsWith(sortKey))
      .reduce((sum, key) => sum + ((b[key] as number) || 0), 0);
    const aIndex = (a.index as number) || 0;
    const bIndex = (b.index as number) || 0;

    const aXVal = a[xAxisKeys[0]] as string | number;
    const bXVal = b[xAxisKeys[0]] as string | number;
    // sorting by x axis
    if (sortBy[0]?.sortType === 'alphabetical') {
      if (typeof aXVal === 'string') {
        return (aXVal as string).localeCompare(bXVal as string);
      }
      return (aXVal as number) - (bXVal as number);
    }

    // sorting by y axis
    if (aValue === bValue) {
      if (aIndex === bIndex) {
        if (typeof aXVal === 'string') {
          return (aXVal as string).localeCompare(bXVal as string);
        }
        return (aXVal as number) - (bXVal as number);
      }
      return aIndex - bIndex;
    }
    return aValue - bValue;
  });

  if (sortBy[0].method === 'desc') {
    preparedData.reverse();
  }
  return preparedData;
};

type BarChartOptions = {
  data: MetricDatum[];
  groupBy?: GroupByOption[];
  scales?: BarChartScalesConfig;
  stackGroups?: string[][];
  xAxisKeys: string[];
  xAxisLabels?: string[];
  yAxisKeys: string[];
  sortBy?: SortByOption[];
  aggregateBy?: AggregateByOption[];
  yAxisLabels: string[];
  showFullLabels?: boolean;
  angleLabels?: boolean;
  topNBars?: number;
  aggregatedBarLabel?: string;
  transformNegativeValues?: boolean;
};

const applyTopNBarsFilter = (
  data: MetricDatum[],
  xAxisKeys: string[],
  yAxisKeys: string[],
  topNBars: number,
  aggregatedLabel?: string,
): MetricDatum[] => {
  if (data.length <= topNBars) {
    return data;
  }

  // Sort by absolute value descending
  const sorted = [...data].sort((a, b) => {
    const aValue = Math.abs((a[yAxisKeys[0]] as number) || 0);
    const bValue = Math.abs((b[yAxisKeys[0]] as number) || 0);
    return bValue - aValue;
  });

  const topN = sorted.slice(0, topNBars);
  const remaining = sorted.slice(topNBars);

  // Create aggregated bar
  const aggregatedValue = remaining.reduce((sum, datum) => {
    return sum + ((datum[yAxisKeys[0]] as number) || 0);
  }, 0);

  // Generate label with count - only replace {count} placeholder if present
  const count = remaining.length;
  const label = aggregatedLabel
    ? aggregatedLabel.replace('{count}', count.toString())
    : `Sum of ${count} other bar${count > 1 ? 's' : ''}`;

  const aggregatedBar: MetricDatum = {
    index: -1,
    [xAxisKeys[0]]: label,
    [yAxisKeys[0]]: aggregatedValue,
  };

  return [...topN, aggregatedBar];
};

/**
 * Transforms data with all-negative values to use absolute values for rendering
 * while preserving original negative values for display purposes.
 * This allows bars to extend naturally left-to-right while still showing negative values.
 * NOTE: this can get complicated quickly, but this use case is specifically for handling
 * SHAP values which are negative. So, this is a pretty niche use case that we can
 * expand upon later if needed
 */
const transformNegativeValues = (
  data: MetricDatum[],
  yAxisKeys: string[],
): { data: MetricDatum[]; hasNegativeTransform: boolean } => {
  // Check if all values for all y-axis keys are negative (or zero)
  const allNegative = yAxisKeys.every((key) =>
    data.every((datum) => {
      const value = datum[key];
      return typeof value !== 'number' || value <= 0;
    }),
  );

  if (!allNegative) {
    return { data, hasNegativeTransform: false };
  }

  // Transform data: store original values and replace with absolute values
  const transformedData = data.map((datum) => {
    const newDatum = { ...datum };
    yAxisKeys.forEach((key) => {
      const value = datum[key];
      if (typeof value === 'number') {
        // Store original negative value
        newDatum[`_original_${key}`] = value;
        // Use absolute value for rendering
        newDatum[key] = Math.abs(value);
      }
    });
    return newDatum;
  });

  return { data: transformedData, hasNegativeTransform: true };
};

export const useBarChart = ({
  data,
  groupBy,
  scales,
  stackGroups,
  xAxisKeys,
  xAxisLabels,
  yAxisKeys,
  yAxisLabels,
  sortBy,
  aggregateBy,
  showFullLabels,
  angleLabels,
  topNBars,
  aggregatedBarLabel,
  transformNegativeValues: shouldTransformNegativeValues = false,
}: BarChartOptions) => {
  if (!yAxisKeys) {
    return {
      data,
      formatValue: () => '',
      hasMultipleSeries: false,
      stackGroups,
      xAxisKeys,
      xAxisLabels: xAxisLabels || [],
      xAxisLabelSpacing: () => 0,
      yAxisKeys,
      yAxisLabels,
      yAxisWidth: 0,
    };
  }

  const hasMultipleSeries = yAxisKeys.length > 1;

  // Transform negative values before any other processing (opt-in feature)
  const { data: transformedData, hasNegativeTransform } = shouldTransformNegativeValues
    ? transformNegativeValues(data, yAxisKeys)
    : { data, hasNegativeTransform: false };

  // Create format function that handles negative value display
  const formatValue = (value: any, dataPoint?: any, key?: string) => {
    let displayValue = value;

    // If we transformed negative values and have the original value, use it for display
    if (hasNegativeTransform && dataPoint && key) {
      const originalKey = `_original_${key}`;
      if (originalKey in dataPoint) {
        displayValue = dataPoint[originalKey];
      }
    } else if (hasNegativeTransform && typeof value === 'number') {
      // If no dataPoint provided, just negate the value
      displayValue = -Math.abs(value);
    }

    return formatNumber({
      value: displayValue,
      format: scales?.y?.tickFormat,
      aggregationOptions: aggregateBy,
    });
  };
  const yAxisWidth = calculateYAxisWidth(
    transformedData,
    yAxisKeys,
    formatValue,
    CHAR_WIDTH_PX,
    angleLabels ? MINIMUM_TICK_WIDTH_ROTATED : MINIMUM_TICK_WIDTH_STRAIGHT,
  );
  const xAxisLabelSpacing = (key: string | number) =>
    calculateXAxisLabelSpacing(
      transformedData,
      key,
      CHAR_WIDTH_PX,
      angleLabels ? X_AXIS_LABEL_ANGLE_ROTATED : X_AXIS_LABEL_ANGLE_STRAIGHT,
      showFullLabels,
    );

  let formattedChartObject = {
    data: transformedData,
    formatValue,
    hasMultipleSeries,
    stackGroups,
    xAxisKeys,
    xAxisLabelSpacing,
    yAxisKeys,
    xAxisLabels: xAxisLabels || [],
    yAxisLabels,
    yAxisWidth,
  };

  if (groupBy?.length) {
    formattedChartObject = {
      formatValue,
      hasMultipleSeries,
      xAxisKeys,
      xAxisLabels: xAxisLabels || [],
      xAxisLabelSpacing,
      yAxisWidth,
      ...prepareGroupedChart(transformedData, groupBy, stackGroups, xAxisKeys, yAxisKeys, yAxisLabels),
    };
  }

  formattedChartObject.data = sortData(formattedChartObject.data, xAxisKeys, sortBy);

  // Apply topNBars filter (only for simple/non-stacked bars)
  if (topNBars && !groupBy?.length && !stackGroups?.length && yAxisKeys.length === 1) {
    formattedChartObject.data = applyTopNBarsFilter(
      formattedChartObject.data,
      xAxisKeys,
      yAxisKeys,
      topNBars,
      aggregatedBarLabel,
    );
  }

  return formattedChartObject;
};
